import React, { useState, useRef, useEffect } from 'react';

interface BuildingPropertyData {
  name?: string;
  BUD?: string;
  floor?: number;
  height?: number;
  OBJECTID?: any;
  address?: string;
  type?: string;
  area?: number;
  buildYear?: number;
  proName?: string;
  cityName?: string;
  couName?: string;
  [key: string]: any;
}

interface BuildingInfoProps {
  buildingData: BuildingPropertyData | null;
  position: { x: number; y: number };
  visible: boolean;
  onClose: () => void;
}

const BuildingInfo: React.FC<BuildingInfoProps> = ({ 
  buildingData, 
  position, 
  visible,
  onClose
}) => {
  // 拖动相关状态
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const popupRef = useRef<HTMLDivElement>(null);

  // 全局鼠标移动和释放事件（拖动）
  useEffect(() => {
    if (!dragging) return;
    const currentOffset = dragOffset;

    const handleMouseMove = (e: MouseEvent) => {
      let newX = e.clientX - currentOffset.x;
      let newY = e.clientY - currentOffset.y;

      if (popupRef.current) {
        const rect = popupRef.current.getBoundingClientRect();
        const winWidth = window.innerWidth;
        const winHeight = window.innerHeight;
        newX = Math.max(0, Math.min(newX, winWidth - rect.width));
        newY = Math.max(0, Math.min(newY, winHeight - rect.height));
      }
      setPopupPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => setDragging(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dragOffset]);

  // 弹窗初始位置
  useEffect(() => {
    if (visible) {
      setPopupPosition({ x: position.x + 20, y: position.y + 10 });
    }
  }, [visible, position]);

  // 鼠标按下开始拖动
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!popupRef.current) return;
    const target = e.target as HTMLElement;
    if (!target.closest('.drag-handle')) return;

    setDragging(true);
    const rect = popupRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    e.preventDefault();
  };

  if (!visible || !buildingData) return null;

  // 数据准备
  const displayName = buildingData.name || buildingData.BUD || '未命名建筑物';
  const heightInfo = buildingData.height 
    ? `${buildingData.height.toFixed(1)}m` 
    : buildingData.floor ? `~${(buildingData.floor * 3).toFixed(0)}m` : '-';
  const floorInfo = buildingData.floor ? `${buildingData.floor}层` : '-';

  // 属性项组件
  const PropertyItem = ({ icon, label, value }: { icon: string; label: string; value: string }) => (
    <div className="flex items-center gap-3 py-2">
      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-blue-500 text-sm`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-400">{label}</div>
        <div className="text-sm text-slate-700 font-medium truncate">{value}</div>
      </div>
    </div>
  );

  return (
    <div 
      ref={popupRef}
      className="absolute z-[40] w-[280px] select-none"
      style={{
        left: `${popupPosition.x}px`,
        top: `${popupPosition.y}px`,
        cursor: dragging ? 'grabbing' : 'default'
      }}
      onMouseDown={handleMouseDown}
    >
      {/* 简约卡片 */}
      <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-slate-200 shadow-lg overflow-hidden">
        {/* 可拖拽标题栏 */}
        <div className="drag-handle px-4 py-3 border-b border-slate-100 bg-slate-50/80 cursor-grab active:cursor-grabbing flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-4 bg-blue-500 rounded-full"></div>
            <h3 className="text-sm font-semibold text-slate-700">建筑物信息</h3>
          </div>
          {/* 关闭按钮 */}
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            aria-label="关闭"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>
        
        {/* 内容区 */}
        <div className="p-4">
          {/* 名称 */}
          <div className="mb-4 pb-3 border-b border-slate-100">
            <div className="text-base font-semibold text-slate-800 leading-tight">{displayName}</div>
          </div>
          
          {/* 属性列表 */}
          <div className="space-y-1">
            <PropertyItem icon="ri-building-line" label="楼层" value={floorInfo} />
            <PropertyItem icon="ri-ruler-line" label="高度" value={heightInfo} />
            
            {/* 行政区划 */}
            {(buildingData.proName || buildingData.cityName || buildingData.couName) && (
              <div className="pt-2 mt-2 border-t border-slate-100">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <i className="ri-map-pin-line"></i>
                  <span>
                    {[buildingData.proName, buildingData.cityName, buildingData.couName].filter(Boolean).join(' · ')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildingInfo;